import { useState } from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { resolveTimeZone, ymdInTimeZone } from "@/lib/timezone";

// Calendar day labels as local Date(y, m-1, d). rangeToParams reads those
// components back to YYYY-MM-DD; the backend interprets them in the account
// System Settings timezone — so "Today" must use that TZ, not the browser's.

/** Build a local Date whose getFullYear/Month/Date equal the given YYYY-MM-DD. */
export const dateFromYmd = (ymd) => {
  const m = String(ymd || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 0, 0, 0, 0);
};

const addCalendarDays = (ymd, n) => {
  const d = dateFromYmd(ymd);
  d.setDate(d.getDate() + n);
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${mo}-${day}`;
};

/** Account-TZ "today" as a {from,to} range for the picker. */
export const todayRange = (timeZone = "UTC") => {
  const ymd = ymdInTimeZone(new Date(), resolveTimeZone(timeZone));
  const t = dateFromYmd(ymd);
  return { from: t, to: t };
};

/** Inclusive last-N account-TZ calendar days (avoids empty lists around UTC midnight). */
export const lastNDaysRange = (timeZone = "UTC", days = 2) => {
  const tz = resolveTimeZone(timeZone);
  const n = Math.max(1, Number(days) || 1);
  const toYmd = ymdInTimeZone(new Date(), tz);
  const fromYmd = addCalendarDays(toYmd, -(n - 1));
  return { from: dateFromYmd(fromYmd), to: dateFromYmd(toYmd) };
};

const presetsForTz = (timeZone) => {
  const tz = resolveTimeZone(timeZone);
  const today = ymdInTimeZone(new Date(), tz);
  return [
    { label: "Today", get: () => { const t = dateFromYmd(today); return { from: t, to: t }; } },
    {
      label: "Yesterday",
      get: () => {
        const y = dateFromYmd(addCalendarDays(today, -1));
        return { from: y, to: y };
      },
    },
    {
      label: "Last 7 days",
      get: () => ({
        from: dateFromYmd(addCalendarDays(today, -6)),
        to: dateFromYmd(today),
      }),
    },
    {
      label: "Last 30 days",
      get: () => ({
        from: dateFromYmd(addCalendarDays(today, -29)),
        to: dateFromYmd(today),
      }),
    },
    {
      label: "This month",
      get: () => {
        const [y, m] = today.split("-").map(Number);
        return {
          from: dateFromYmd(`${y}-${String(m).padStart(2, "0")}-01`),
          to: dateFromYmd(today),
        };
      },
    },
  ];
};

// Calendar day numbers the user picked → YYYY-MM-DD for the backend, which
// interprets them in the account System Settings timezone (not browser/UTC).
export const rangeToParams = (range) => {
  if (!range?.from) return {};
  const ymd = (d) => {
    const x = new Date(d);
    const y = x.getFullYear();
    const m = String(x.getMonth() + 1).padStart(2, "0");
    const day = String(x.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  return {
    start_from: ymd(range.from),
    start_to: ymd(range.to || range.from),
  };
};

const label = (range) => {
  if (!range?.from) return "Select dates";
  const f = format(range.from, "MMM d, yyyy");
  if (!range.to || format(range.to, "yyyy-MM-dd") === format(range.from, "yyyy-MM-dd")) return f;
  return `${f} — ${format(range.to, "MMM d, yyyy")}`;
};

export default function DateRangePicker({
  value,
  onChange,
  testid = "date-range",
  timeZone = "UTC",
}) {
  const [open, setOpen] = useState(false);
  const presets = presetsForTz(timeZone);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" data-testid={`${testid}-trigger`}
          className="h-9 justify-start rounded-md font-normal text-sm w-full">
          <CalendarIcon className="w-4 h-4 mr-2 shrink-0" />
          <span className="truncate">{label(value)}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start" data-testid={`${testid}-popover`}>
        <div className="flex flex-col sm:flex-row">
          <div className="flex sm:flex-col gap-1 p-3 border-b sm:border-b-0 sm:border-r border-border flex-wrap">
            {presets.map((p) => (
              <Button key={p.label} variant="ghost" size="sm"
                data-testid={`${testid}-preset-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="justify-start rounded-md text-sm font-normal"
                onClick={() => { onChange(p.get()); setOpen(false); }}>
                {p.label}
              </Button>
            ))}
          </div>
          <Calendar mode="range" numberOfMonths={2} selected={value}
            defaultMonth={value?.from}
            onSelect={(r) => onChange(r || {})} />
        </div>
      </PopoverContent>
    </Popover>
  );
}
